/**
 * Biome definitions.
 *
 * A biome is selected from environmental variables (temperature, humidity,
 * elevation, slope, latitude), not painted onto a map. That means the same
 * classifier runs on every body: a cold, dry, high-altitude point becomes alpine
 * terrain on Halcyon and on Meridia alike, and each planet's distinctiveness
 * comes from which biomes it permits and what its climate actually produces.
 */

export interface BiomeDef {
  id: string;
  name: string;
  /** Base surface albedo, linear RGB. */
  color: [number, number, number];
  /** Secondary colour blended in by fine-scale noise, giving local variation. */
  detailColor: [number, number, number];
  /** PBR roughness, 0..1. */
  roughness: number;
  /** PBR metalness, 0..1. Almost everything natural is 0. */
  metalness: number;
  /** Vegetation instances per square kilometre. Zero for barren biomes. */
  vegetationDensity: number;
  /** Scatter (rock/debris) instances per square kilometre. */
  scatterDensity: number;
  /** Preferred temperature band, kelvin. */
  temperature: [number, number];
  /** Preferred humidity band, 0..1. */
  humidity: [number, number];
  /** Preferred elevation band as a fraction of the relief budget, -1..1. */
  elevation: [number, number];
  /** Maximum slope in radians at which this biome can form. */
  maxSlope: number;
  /** Traversal difficulty multiplier for ground vehicles and on foot. */
  traversalCost: number;
  /** Environmental hazard rating, 0..1. */
  hazard: number;
  /** Ambient audio bed id. */
  ambience: string;
  description: string;
}

const DEG = Math.PI / 180;

export const BIOMES: BiomeDef[] = [
  {
    id: 'ocean',
    name: 'Ocean',
    color: [0.012, 0.05, 0.11],
    detailColor: [0.03, 0.12, 0.2],
    roughness: 0.08,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 0,
    temperature: [250, 340],
    humidity: [0.9, 1],
    elevation: [-1, -0.02],
    maxSlope: Math.PI,
    traversalCost: 3,
    hazard: 0.3,
    ambience: 'ocean',
    description: 'Open water. Deep basins are effectively unexplored without submersible equipment.',
  },
  {
    id: 'coastal',
    name: 'Coastal',
    color: [0.62, 0.57, 0.42],
    detailColor: [0.72, 0.68, 0.54],
    roughness: 0.78,
    metalness: 0,
    vegetationDensity: 380,
    scatterDensity: 900,
    temperature: [265, 320],
    humidity: [0.55, 1],
    elevation: [-0.03, 0.04],
    maxSlope: 22 * DEG,
    traversalCost: 1,
    hazard: 0.1,
    ambience: 'shore',
    description: 'Sand and shingle at the waterline, with tidal flats and drift material.',
  },
  {
    id: 'temperateForest',
    name: 'Temperate Forest',
    color: [0.15, 0.26, 0.11],
    detailColor: [0.24, 0.33, 0.15],
    roughness: 0.86,
    metalness: 0,
    vegetationDensity: 5200,
    scatterDensity: 1400,
    temperature: [275, 300],
    humidity: [0.45, 0.85],
    elevation: [0.0, 0.35],
    maxSlope: 34 * DEG,
    traversalCost: 1.4,
    hazard: 0.05,
    ambience: 'forest',
    description: 'Mixed broadleaf and conifer cover over deep soil.',
  },
  {
    id: 'rainforest',
    name: 'Rainforest',
    color: [0.07, 0.2, 0.06],
    detailColor: [0.13, 0.28, 0.09],
    roughness: 0.9,
    metalness: 0,
    vegetationDensity: 9800,
    scatterDensity: 900,
    temperature: [292, 318],
    humidity: [0.75, 1],
    elevation: [0.0, 0.22],
    maxSlope: 38 * DEG,
    traversalCost: 2.2,
    hazard: 0.25,
    ambience: 'jungle',
    description: 'Closed multi-layer canopy with near-continuous humidity.',
  },
  {
    id: 'jungleCanopy',
    name: 'High Canopy',
    color: [0.05, 0.17, 0.05],
    detailColor: [0.1, 0.26, 0.08],
    roughness: 0.92,
    metalness: 0,
    vegetationDensity: 14000,
    scatterDensity: 400,
    temperature: [295, 322],
    humidity: [0.8, 1],
    elevation: [0.02, 0.3],
    maxSlope: 30 * DEG,
    traversalCost: 3.0,
    hazard: 0.4,
    ambience: 'jungle',
    description: 'Emergent trees reaching ninety metres. Ground level is permanently dim.',
  },
  {
    id: 'grassland',
    name: 'Grassland',
    color: [0.29, 0.33, 0.15],
    detailColor: [0.4, 0.42, 0.2],
    roughness: 0.82,
    metalness: 0,
    vegetationDensity: 1400,
    scatterDensity: 600,
    temperature: [275, 305],
    humidity: [0.28, 0.55],
    elevation: [0.0, 0.28],
    maxSlope: 20 * DEG,
    traversalCost: 0.9,
    hazard: 0.03,
    ambience: 'plains',
    description: 'Open grass and low scrub. The easiest terrain in the system to drive across.',
  },
  {
    id: 'savannah',
    name: 'Savannah',
    color: [0.42, 0.38, 0.18],
    detailColor: [0.52, 0.45, 0.24],
    roughness: 0.84,
    metalness: 0,
    vegetationDensity: 700,
    scatterDensity: 800,
    temperature: [295, 318],
    humidity: [0.2, 0.42],
    elevation: [0.0, 0.24],
    maxSlope: 22 * DEG,
    traversalCost: 1.0,
    hazard: 0.06,
    ambience: 'plains',
    description: 'Dry grassland punctuated by isolated drought-adapted trees.',
  },
  {
    id: 'swamp',
    name: 'Swamp',
    color: [0.14, 0.2, 0.1],
    detailColor: [0.2, 0.24, 0.13],
    roughness: 0.7,
    metalness: 0,
    vegetationDensity: 3600,
    scatterDensity: 400,
    temperature: [283, 312],
    humidity: [0.85, 1],
    elevation: [-0.02, 0.06],
    maxSlope: 8 * DEG,
    traversalCost: 2.6,
    hazard: 0.22,
    ambience: 'swamp',
    description: 'Waterlogged ground and standing pools. Vehicles bog down; walking is slow.',
  },
  {
    id: 'desert',
    name: 'Desert',
    color: [0.68, 0.5, 0.29],
    detailColor: [0.76, 0.6, 0.37],
    roughness: 0.76,
    metalness: 0,
    vegetationDensity: 40,
    scatterDensity: 300,
    temperature: [250, 340],
    humidity: [0.0, 0.14],
    elevation: [0.0, 0.3],
    maxSlope: 26 * DEG,
    traversalCost: 1.3,
    hazard: 0.18,
    ambience: 'desert',
    description: 'Sand sheets and deflation surfaces. Dune fields dominate the low basins.',
  },
  {
    id: 'dunes',
    name: 'Dune Field',
    color: [0.74, 0.55, 0.32],
    detailColor: [0.82, 0.64, 0.4],
    roughness: 0.68,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 40,
    temperature: [230, 345],
    humidity: [0.0, 0.1],
    elevation: [-0.05, 0.14],
    maxSlope: 32 * DEG,
    traversalCost: 2.1,
    hazard: 0.22,
    ambience: 'desert',
    description: 'Mobile transverse dunes. Landing on a slip face is a poor idea.',
  },
  {
    id: 'rockyDesert',
    name: 'Rocky Desert',
    color: [0.42, 0.35, 0.28],
    detailColor: [0.52, 0.45, 0.37],
    roughness: 0.88,
    metalness: 0,
    vegetationDensity: 10,
    scatterDensity: 2600,
    temperature: [150, 340],
    humidity: [0.0, 0.2],
    elevation: [0.02, 0.5],
    maxSlope: 42 * DEG,
    traversalCost: 1.5,
    hazard: 0.12,
    ambience: 'barren',
    description: 'Bedrock pavement littered with weathered float rock. Good prospecting ground.',
  },
  {
    id: 'saltFlat',
    name: 'Salt Flat',
    color: [0.82, 0.8, 0.74],
    detailColor: [0.9, 0.89, 0.85],
    roughness: 0.42,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 20,
    temperature: [230, 340],
    humidity: [0.0, 0.25],
    elevation: [-0.04, 0.03],
    maxSlope: 4 * DEG,
    traversalCost: 0.7,
    hazard: 0.08,
    ambience: 'barren',
    description: 'Evaporite pan, exceptionally flat and hard. The best natural landing surface anywhere.',
  },
  {
    id: 'tundra',
    name: 'Tundra',
    color: [0.4, 0.4, 0.34],
    detailColor: [0.5, 0.5, 0.44],
    roughness: 0.84,
    metalness: 0,
    vegetationDensity: 220,
    scatterDensity: 700,
    temperature: [220, 275],
    humidity: [0.25, 0.7],
    elevation: [0.0, 0.35],
    maxSlope: 24 * DEG,
    traversalCost: 1.2,
    hazard: 0.15,
    ambience: 'wind',
    description: 'Frozen ground with sparse low vegetation and patterned polygonal soil.',
  },
  {
    id: 'glacier',
    name: 'Glacier',
    color: [0.78, 0.84, 0.9],
    detailColor: [0.88, 0.93, 0.98],
    roughness: 0.22,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 30,
    temperature: [80, 265],
    humidity: [0.4, 1],
    elevation: [0.05, 1],
    maxSlope: 44 * DEG,
    traversalCost: 1.9,
    hazard: 0.3,
    ambience: 'wind',
    description: 'Flowing ice with crevasse fields. Surface stability cannot be assumed.',
  },
  {
    id: 'iceField',
    name: 'Ice Field',
    color: [0.82, 0.87, 0.92],
    detailColor: [0.92, 0.95, 1.0],
    roughness: 0.18,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 120,
    temperature: [40, 200],
    humidity: [0.2, 1],
    elevation: [-1, 1],
    maxSlope: 30 * DEG,
    traversalCost: 1.4,
    hazard: 0.2,
    ambience: 'wind',
    description: 'Consolidated volatile ice, often layered over older impact terrain.',
  },
  {
    id: 'alpine',
    name: 'Alpine',
    color: [0.36, 0.34, 0.3],
    detailColor: [0.56, 0.56, 0.56],
    roughness: 0.9,
    metalness: 0,
    vegetationDensity: 120,
    scatterDensity: 3000,
    temperature: [230, 285],
    humidity: [0.2, 0.8],
    elevation: [0.4, 1],
    maxSlope: 60 * DEG,
    traversalCost: 2.4,
    hazard: 0.28,
    ambience: 'wind',
    description: 'Exposed high ground: scree, frost-shattered rock, and permanent snow on the summits.',
  },
  {
    id: 'volcanic',
    name: 'Volcanic',
    color: [0.2, 0.15, 0.13],
    detailColor: [0.32, 0.22, 0.18],
    roughness: 0.92,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 1800,
    temperature: [300, 900],
    humidity: [0.0, 0.4],
    elevation: [0.0, 0.7],
    maxSlope: 48 * DEG,
    traversalCost: 1.8,
    hazard: 0.7,
    ambience: 'volcanic',
    description: 'Recent flows, spatter cones and thermally active ground.',
  },
  {
    id: 'obsidianFlow',
    name: 'Obsidian Flow',
    color: [0.08, 0.07, 0.08],
    detailColor: [0.16, 0.13, 0.15],
    roughness: 0.24,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 600,
    temperature: [400, 1100],
    humidity: [0.0, 0.2],
    elevation: [-0.2, 0.3],
    maxSlope: 20 * DEG,
    traversalCost: 2.0,
    hazard: 0.85,
    ambience: 'volcanic',
    description: 'Glassy quenched lava. Reflective, sharp, and often only a thin crust over molten rock.',
  },
  {
    id: 'ashPlain',
    name: 'Ash Plain',
    color: [0.26, 0.23, 0.21],
    detailColor: [0.36, 0.32, 0.29],
    roughness: 0.94,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 200,
    temperature: [300, 800],
    humidity: [0.0, 0.3],
    elevation: [-0.3, 0.2],
    maxSlope: 12 * DEG,
    traversalCost: 1.5,
    hazard: 0.5,
    ambience: 'volcanic',
    description: 'Deep pyroclastic fall deposits. Soft underfoot and prone to swallowing landing gear.',
  },
  {
    id: 'crystalField',
    name: 'Crystal Field',
    color: [0.3, 0.36, 0.44],
    detailColor: [0.5, 0.6, 0.72],
    roughness: 0.14,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 4200,
    temperature: [60, 700],
    humidity: [0.0, 0.5],
    elevation: [-0.2, 0.6],
    maxSlope: 40 * DEG,
    traversalCost: 1.7,
    hazard: 0.35,
    ambience: 'crystal',
    description: 'Mineralised ground where slow cooling has grown metre-scale crystal habits.',
  },
  {
    id: 'toxicWaste',
    name: 'Toxic Waste',
    color: [0.5, 0.32, 0.14],
    detailColor: [0.6, 0.4, 0.2],
    roughness: 0.8,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 500,
    temperature: [300, 800],
    humidity: [0.0, 0.4],
    elevation: [-0.15, 0.3],
    maxSlope: 30 * DEG,
    traversalCost: 1.6,
    hazard: 0.9,
    ambience: 'toxic',
    description: 'Chemically active surface that attacks seals, optics and unprotected alloys.',
  },
  {
    id: 'corrosionScar',
    name: 'Corrosion Scar',
    color: [0.08, 0.05, 0.04],
    detailColor: [0.16, 0.08, 0.05],
    roughness: 0.5,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 60,
    temperature: [500, 900],
    humidity: [0.0, 1.0],
    elevation: [-1, -0.18],
    maxSlope: 10 * DEG,
    traversalCost: 2.6,
    hazard: 0.98,
    ambience: 'toxic',
    description: 'Corrosive condensate pooled across low basins, dark enough to look wet under the haze.',
  },
  {
    id: 'sulfurCrust',
    name: 'Sulfur Crust',
    color: [0.82, 0.72, 0.46],
    detailColor: [0.92, 0.85, 0.6],
    roughness: 0.46,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 260,
    temperature: [500, 900],
    humidity: [0.0, 1.0],
    elevation: [0.22, 1],
    maxSlope: 45 * DEG,
    traversalCost: 1.4,
    hazard: 0.55,
    ambience: 'toxic',
    description: 'Wind-scoured sulfur and mineral crust baked pale on exposed high ground.',
  },
  {
    id: 'radioactive',
    name: 'Radioactive Zone',
    color: [0.3, 0.34, 0.22],
    detailColor: [0.42, 0.48, 0.3],
    roughness: 0.86,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 1600,
    temperature: [100, 400],
    humidity: [0.0, 0.5],
    elevation: [-0.2, 0.6],
    maxSlope: 40 * DEG,
    traversalCost: 1.5,
    hazard: 0.95,
    ambience: 'barren',
    description: 'Exposed high-activity mineralisation. Dose rate rises sharply toward the vein centres.',
  },
  {
    id: 'fungal',
    name: 'Fungal Basin',
    color: [0.26, 0.18, 0.24],
    detailColor: [0.4, 0.26, 0.34],
    roughness: 0.74,
    metalness: 0,
    vegetationDensity: 4200,
    scatterDensity: 700,
    temperature: [285, 315],
    humidity: [0.7, 1],
    elevation: [-0.06, 0.1],
    maxSlope: 20 * DEG,
    traversalCost: 1.9,
    hazard: 0.45,
    ambience: 'jungle',
    description: 'Sheltered basins colonised by large fruiting bodies and mycelial mats.',
  },
  {
    id: 'alienOrganic',
    name: 'Alien Organic',
    color: [0.18, 0.24, 0.3],
    detailColor: [0.28, 0.4, 0.44],
    roughness: 0.5,
    metalness: 0,
    vegetationDensity: 2600,
    scatterDensity: 900,
    temperature: [90, 320],
    humidity: [0.5, 1],
    elevation: [-0.2, 0.3],
    maxSlope: 34 * DEG,
    traversalCost: 2.0,
    hazard: 0.55,
    ambience: 'alien',
    description: 'Structured biological growth that does not map onto any known taxonomy.',
  },
  {
    id: 'karst',
    name: 'Karst',
    color: [0.5, 0.48, 0.42],
    detailColor: [0.62, 0.6, 0.54],
    roughness: 0.88,
    metalness: 0,
    vegetationDensity: 900,
    scatterDensity: 2200,
    temperature: [270, 320],
    humidity: [0.4, 0.95],
    elevation: [0.02, 0.4],
    maxSlope: 55 * DEG,
    traversalCost: 2.3,
    hazard: 0.35,
    ambience: 'cave',
    description: 'Dissolution topography: towers, sinkholes and extensive cave development beneath.',
  },
  {
    id: 'regolith',
    name: 'Regolith',
    color: [0.34, 0.33, 0.31],
    detailColor: [0.44, 0.43, 0.41],
    roughness: 0.95,
    metalness: 0,
    vegetationDensity: 0,
    scatterDensity: 1900,
    temperature: [20, 400],
    humidity: [0, 0.05],
    elevation: [-1, 1],
    maxSlope: 34 * DEG,
    traversalCost: 1.1,
    hazard: 0.1,
    ambience: 'vacuum',
    description: 'Impact-comminuted dust and breccia, undisturbed for hundreds of millions of years.',
  },
  {
    id: 'cave',
    name: 'Cave',
    color: [0.16, 0.15, 0.14],
    detailColor: [0.26, 0.24, 0.22],
    roughness: 0.9,
    metalness: 0,
    vegetationDensity: 60,
    scatterDensity: 1200,
    temperature: [80, 400],
    humidity: [0.2, 1],
    elevation: [-1, 1],
    maxSlope: Math.PI,
    traversalCost: 2.0,
    hazard: 0.4,
    ambience: 'cave',
    description: 'Subsurface void systems. Shielded from radiation and weather, and often mineral rich.',
  },
];

const BY_ID = new Map(BIOMES.map((b) => [b.id, b]));

export function getBiome(id: string): BiomeDef {
  const b = BY_ID.get(id);
  if (!b) throw new Error(`Unknown biome "${id}"`);
  return b;
}

export function findBiome(id: string): BiomeDef | undefined {
  return BY_ID.get(id);
}

/** Stable numeric index, used to pack biome identity into vertex attributes. */
export const BIOME_INDEX = new Map(BIOMES.map((b, i) => [b.id, i]));
export const BIOME_BY_INDEX = BIOMES;
